/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import axios from 'axios';
import { makeApiPath } from '../utils/apiUtils';
import logger from '../utils/logger';

export interface ChallengeSubmission {
  topicId: string;
  explanationIndex: number;
  challengeText: string;
  challengeType: 'refutation' | 'replacement' | 'evidence_contradiction';
}

export interface Challenge {
  _id: string;
  targetTopicId: string;
  targetExplanationIndex: number;
  challengerId: string;
  challengeText: string;
  challengeType: 'refutation' | 'replacement' | 'evidence_contradiction';
  status: 'pending' | 'ai_defense' | 'awaiting_human_defense' | 'human_defense' | 'completed';
  paymentType: 'free' | 'points' | 'money';
  paymentAmount: number;
  refunded?: boolean;
  createdAt: string;
  completedAt?: string;
  finalResult?: 'explanation_maintained' | 'explanation_challenged' | 'explanation_updated';
  result?: 'explanation_maintained' | 'explanation_challenged' | 'explanation_updated';
  relationshipType?: 'submitted' | 'received' | 'available';
  topicName?: string;
  explanationText?: string;
  // AI Assessment fields
  aiAssessment?: {
    challengeSuccessful: boolean;
    reasoning: string;
    completedAt: Date;
  };
  // Debate links
  aiDefenseDebateId?: string;
  humanDefenseDebateId?: string;
}

/**
 * Submit a new challenge to a CUBE explanation
 */
export const submitChallenge = async (challengeData: ChallengeSubmission): Promise<{ challengeId: string; debateId: string | null; paymentType: string; paymentAmount: number }> => {
  try {
    const response = await axios.post(makeApiPath('/challenges'), challengeData);
    return response.data;
  } catch (error) {
    logger.error('Error submitting challenge:', error);
    throw error;
  }
};

/**
 * Get challenges for the authenticated user
 */
export const getUserChallenges = async (type: 'submitted' | 'received' | 'available' | 'all' = 'all'): Promise<Challenge[]> => {
  try {
    const response = await axios.get(makeApiPath(`/challenges/user?type=${type}`));
    return response.data.challenges;
  } catch (error) {
    logger.error('Error fetching user challenges:', error);
    throw error;
  }
};

/**
 * Accept a human defense challenge
 */
export const acceptHumanDefense = async (challengeId: string): Promise<{ debateId: string; debateInviteCode: string }> => {
  try {
    const response = await axios.post(makeApiPath(`/challenges/${challengeId}/accept-defense`));
    return response.data;
  } catch (error) {
    logger.error('Error accepting human defense:', error);
    throw error;
  }
};

/**
 * Complete a challenge (admin/system function)
 */
export const completeChallenge = async (
  challengeId: string, 
  result: 'explanation_maintained' | 'explanation_challenged' | 'explanation_updated',
  reason: string,
  aiUpdatedExplanation?: string
): Promise<void> => {
  try {
    await axios.post(makeApiPath(`/challenges/${challengeId}/complete`), {
      result,
      reason,
      aiUpdatedExplanation
    });
  } catch (error) {
    logger.error('Error completing challenge:', error);
    throw error;
  }
}; 